import { X } from "lucide-react";
import type { ReactNode } from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { closeCalendarOverlay, isCalendarOverlayOpen } from "../../../../../lib/calendarOverlay";
import { useDraggableDialog } from "./useDraggableDialog";

// ---------------------------------------------------------------------------
// CallScriptDialogShell
//
// The outer chrome both Call Script modals share: a wide, drag-by-the-header,
// deliberately non-dismissable dialog.
//
// Three behaviours worth knowing:
//
//   * `modal={false}` — a product ask: the page behind the call script must
//     stay usable while a call is in progress. It also sidesteps a uikit
//     DatePicker bug: the picker portals its calendar from its own bundled
//     copy of Radix, which shares no layer or focus state with this Dialog's,
//     so under modal mode the calendar inherits the `pointer-events: none`
//     Radix puts on <body> and its day cells can't be clicked.
//
//     Be aware of what non-modal mode gives up, though:
//       - No scrim. Radix's DialogOverlay returns null outright when not
//         modal (`context.modal ? <Presence/> : null` — `forceMount` does
//         not override it), so nothing visually separates the dialog from
//         the page.
//       - No focus trap, scroll lock, or `aria-hidden` on the rest of the
//         page. Keyboard and screen-reader users can tab/read straight out
//         of the dialog, and the page scrolls underneath it.
//       - The page behind stays fully interactive, so a user can edit or
//         save the underlying application while the script is open, and
//         the two can drift out of sync.
//   * `onInteractOutside` is prevented — a click outside never discards a
//     call in progress.
//   * Escape belongs to the calendar while one is open (see calendarOverlay);
//     otherwise both layers would dismiss on the same keypress, taking the
//     whole script and everything typed into it down with the calendar.
// ---------------------------------------------------------------------------

interface CallScriptDialogShellProperties {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  /** Screen-reader-only description of the dialog. */
  readonly description: string;
  readonly closeLabel: string;
  /** Identifies the drag handle in tests. */
  readonly headerTestId: string;
  /**
   * Everything below the header. Deliberately unstructured: the employment
   * script swaps between a loading skeleton, a retryable error and the form,
   * and owns its own DialogFooter, so the shell imposes no body layout.
   */
  readonly children: ReactNode;
}

export function CallScriptDialogShell({
  open,
  onOpenChange,
  title,
  description,
  closeLabel,
  headerTestId,
  children,
}: CallScriptDialogShellProperties) {
  const { offset, onHeaderPointerDown } = useDraggableDialog(open);

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => {
          if (isCalendarOverlayOpen()) {
            event.preventDefault();
            closeCalendarOverlay();
          }
        }}
        style={{ translate: `calc(-50% + ${offset.x}px) calc(-50% + ${offset.y}px)` }}
        className="gap-(--spacing-0) p-(--spacing-0) overflow-hidden sm:max-w-3xl lg:max-w-5xl"
      >
        <div
          data-testid={headerTestId}
          onPointerDown={onHeaderPointerDown}
          className="bg-primary text-primary-foreground px-(--spacing-4) py-(--spacing-2) flex cursor-grab touch-none select-none items-center justify-between border-b active:cursor-grabbing"
        >
          <DialogTitle className="text-primary-foreground text-(length:--typography-base-sizes-extra-small-font-size)/(--typography-base-sizes-extra-small-line-height) font-(--font-weight-semibold)">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-(--spacing-1) transition-colors"
            aria-label={closeLabel}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>

        {children}
      </DialogContent>
    </Dialog>
  );
}
